import type { ReactNode } from "react";
import { fmtMoney, type BoardRow } from "@/lib/event-board";
import type { WrapUpField } from "@/hooks/useEventsPipeline";
import type { EventBoardHandlers } from "@/hooks/useEventBoardActions";
import { cn } from "@/lib/utils";

interface PostEventChecklistProps {
  row: BoardRow;
  handlers: EventBoardHandlers;
  /** The Mark sent field saving for this event, if any. */
  pendingField: WrapUpField | null;
  donePending: boolean;
  /** Prod doesn't have the wrap-up columns yet (the #1557 migration hasn't deployed). */
  wrapUpUnavailable: boolean;
}

type Tick = "y" | "n" | "owe";

const TICK_CLASS: Record<Tick, string> = {
  y: "bg-[hsl(var(--ev-ok))] text-card",
  n: "border-2 border-border",
  owe: "border-2 border-[hsl(var(--ev-warn))]",
};

const ACTION = "ml-auto p-0 text-[12.5px] font-bold text-accent disabled:cursor-not-allowed disabled:opacity-50";

function Item({ tick, children }: { tick: Tick; children: ReactNode }) {
  return (
    <li className={cn("flex items-center gap-2", tick === "owe" && "font-semibold text-[hsl(var(--ev-warn))]")}>
      <span
        aria-hidden
        className={cn("grid h-4 w-4 flex-none place-items-center rounded-full text-[10px] font-bold", TICK_CLASS[tick])}
      >
        {tick === "y" ? "✓" : ""}
      </span>
      {children}
    </li>
  );
}

/** A Post-event card's wrap-up: the balance, the thank-you, the review ask, then Mark done (#1557). */
export function PostEventChecklist({
  row,
  handlers,
  pendingField,
  donePending,
  wrapUpUnavailable,
}: PostEventChecklistProps) {
  const sentButton = (field: WrapUpField) => (
    <button
      type="button"
      className={ACTION}
      disabled={wrapUpUnavailable || pendingField === field}
      title={wrapUpUnavailable ? "Available once this update is live" : undefined}
      onClick={() => handlers.markSent(row, field)}
    >
      Mark sent
    </button>
  );
  return (
    <>
      <ul className="m-0 grid list-none gap-1 border-t p-0 pt-[7px] text-[13px]">
        <Item tick={row.owing > 0 ? "owe" : "y"}>
          {row.owing > 0 ? `${fmtMoney(row.owing)} balance owing` : "Paid in full"}
          {row.owingQuote && (
            <button type="button" className={ACTION} onClick={() => handlers.recordPayment(row)}>
              Record payment
            </button>
          )}
        </Item>
        <Item tick={row.thanked ? "y" : "n"}>
          {row.thanked ? "Thank-you text sent" : "Thank-you text"}
          {!row.thanked && sentButton("thank_you_sent_at")}
        </Item>
        <Item tick={row.reviewAsked ? "y" : "n"}>
          {row.reviewAsked ? "Review ask sent" : "Review ask"}
          {!row.reviewAsked && sentButton("review_requested_at")}
        </Item>
      </ul>
      <button
        type="button"
        disabled={donePending}
        onClick={() => handlers.markDone(row)}
        className="justify-self-start rounded-md border bg-background px-[9px] py-[3px] text-[12.5px] font-semibold hover:bg-muted disabled:opacity-50"
      >
        Mark done
      </button>
    </>
  );
}
